<template>
  <div class="order-list order-list--b2b">
    <div class="order-list__header-wrapper">
      <Header
        v-if="hasStats"
        :to-hide-total="isAdmin"
        :count="meta.totalCount"
        :amount="meta.totalAmount"
        :filters-applied="filters"
        :show-label="!!totalLabel"
      >
        <template v-if="totalLabel" #total>{{ totalLabel }}</template>
      </Header>
      <div class="order-list__filters">
        <slot v-if="hasSlotFiltersGroup" name="filtersGroup"></slot>
        <ez-filter-list
          v-else
          :filters="filters"
          @resetFilter="resetFilters"
          @filterUpdated="updateFilters"
        >
          <slot name="filters"></slot>
        </ez-filter-list>
        <div class="order-list__filters-action">
          <slot name="actions" />
        </div>
      </div>
    </div>
    <div v-if="hasOrders" class="order-list__content-wrapper">
      <div class="order-list__content">
        <slot name="banner"></slot>
        <div v-for="(group, id) in groupedOrders" :key="id" class="order-list__group">
          <h2>{{ group.title }}</h2>
          <ez-table
            :data="group.items"
            :headers="headers"
            :columns="columns"
            :headerProps="{
              orderedAt: {
                class: 'date-cell medium-cell clickable-cell',
                on: {
                  click: event => {
                    event.stopPropagation();
                    $emit('orderedAt');
                  },
                },
              },
              expectedAt: {
                class: 'date-cell medium-cell clickable-cell',
                on: {
                  click: event => {
                    event.stopPropagation();
                    $emit('expectedAt');
                  },
                },
              },
            }"
            :columnProps="{
              orderNumber: { class: 'orderNumber-cell medium-cell low-priority-cell' },
              invoiceNumber: { class: 'orderNumber-cell large-cell' },
              statusIcon: { class: 'extra-small-cell text-overflow--clip' },
              exportStatus: { class: 'small-cell-large-title low-priority-cell' },
              orderedAt: { class: 'date-cell small-cell' },
              expectedAt: { class: 'date-cell small-cell' },
              status: { class: 'medium-cell' },
              amount: { class: 'price-cell' },
              requestedBy: { class: 'medium-cell' },
              createdByUser: { class: 'small-cell-medium-title low-priority-cell' },
              processingStatus: { class: 'small-cell' },
              invoiceDate: { class: 'date-cell' },
              type: { class: 'medium-cell' },
              venue: { class: 'venue-cell' }
            }"
            @rowClick="onRowClick"
            :removeButton="isInvoice"
            @removeItem="onRemoveRow"
            :hasMinHeight="false"
          >
            <template #cell-orderNumber="{ row }">
              <span
                class="order-number"
                :data-cy="`${commonCy.ORDERS.TEXT__ORDER_NUMBER}-${row.id}`"
              >
                {{ row.orderNumber }}
              </span>
              <span v-if="showInvoiceNumber && (row.invoiceNumber || row.distributorOrderNumber)">
                {{ row.invoiceNumber || row.distributorOrderNumber }}
              </span>
            </template>
            <template #cell-invoiceNumber="{ row }">
              <span
                class="order-number"
                :data-cy="`${commonCy.ORDERS.TEXT__ORDER_NUMBER}-${row.id}`"
              >
                {{ row.invoiceNumber ?? '-' }}
              </span>
            </template>
            <template #cell-statusIcon="{ row }">
              <div class="order-list__status-icons">
                <font-awesome-icon
                  v-if="
                    (row.status === ORDER_STATUS_PENDING ||
                      row.status === ORDER_STATUS_SENT ||
                      row.status === ORDER_STATUS_BACKORDER) &&
                    row.afterWorkingHours
                  "
                  v-tooltip="{
                    placement: 'top-end',
                    content: `This order was placed after work hours.`,
                    classes: ['tooltip-general', 'tooltip--lift-up'],
                  }"
                  :icon="{ prefix: 'fas', iconName: 'clock' }"
                />
                <img
                  v-if="row.invoiceProcessingStatus === INVOICE_PROCESSING_STATUS_PROCESSED"
                  src="@/assets/icons/ic_file-check.svg"
                  alt="Green file icon with white checkmark"
                  :data-cy="`${commonCy.ORDERS.ICON__INVOICE_PROCESSING_STATUS}-processed-${row.id}`"
                  v-tooltip="{
                    placement: 'top-end',
                    content: 'INV processed',
                    classes: ['tooltip-general', 'tooltip--lift-up'],
                  }"
                />
                <img
                  v-if="row.invoiceProcessingStatus === INVOICE_PROCESSING_STATUS_ACTION_NEEDED"
                  src="@/assets/icons/ic_file-exclamation.svg"
                  alt="Red file icon with white exclamation mark"
                  :data-cy="`${commonCy.ORDERS.ICON__INVOICE_PROCESSING_STATUS}-actionNeeded-${row.id}`"
                  v-tooltip="{
                    placement: 'top-end',
                    content: 'INV action needed',
                    classes: ['tooltip-general', 'tooltip--lift-up'],
                  }"
                />
                <img
                  v-if="row.invoiceProcessingStatus === INVOICE_PROCESSING_STATUS_PROCESSING"
                  src="@/assets/icons/ic_file-loading.svg"
                  alt="Blue file icon with white loader"
                  :data-cy="`${commonCy.ORDERS.ICON__INVOICE_PROCESSING_STATUS}-processing-${row.id}`"
                  v-tooltip="{
                    placement: 'top-end',
                    content: 'INV processing',
                    classes: ['tooltip-general', 'tooltip--lift-up'],
                  }"
                />
              </div>
            </template>
            <template #cell-venue="{ row: { venue, id } }">
              <v-venue-entity-info
                :venue="venue || {}"
                :data-cy="`${commonCy.ORDERS.TEXT__OUTLET_NAME}-${id}`"
              />
            </template>
            <template #cell-distributor="{ row: { distributor, groupVenue, id } }">
              <v-distributor-entity-info
                v-if="distributor"
                :distributor="distributor || {}"
                :data-cy="`${commonCy.ORDERS.TEXT__DISTRIBUTOR_NAME}-${id}`"
              />
              <v-venue-entity-info
                v-else-if="isInvoice && groupVenue"
                :venue="groupVenue || {}"
                :data-cy="`${commonCy.ORDERS.TEXT__OUTLET_NAME}-${id}`"
              />
              <span v-else>-</span>
            </template>
            <template #cell-orderedAt="{ row }">
              <span :data-cy="`${commonCy.ORDERS.TEXT__ORDER_DATE}-${row.id}`">
                {{ row.orderedAt | date }}
              </span>
            </template>
            <template #cell-expectedAt="{ row }">
              <span :data-cy="`${commonCy.ORDERS.TEXT__DELIVERY_DATE}-${row.id}`">
                <template v-if="row.status === ORDER_STATUS_QUOTE">-</template>
                <template v-else>{{ row.expectedAt | date }}</template>
              </span>
            </template>
            <template #cell-invoiceDate="{ row }">
              <span :data-cy="`${commonCy.ORDERS.TEXT__DELIVERY_DATE}-${row.id}`">
                <template v-if="row.invoiceDate">{{ row.invoiceDate | dateRefactored }}</template>
                <template v-else>-</template>
              </span>
            </template>
            <template #cell-requestedBy="{ row: { requestedBy, createdAt } }">
              <v-account-owner-entity-info
                :account-owner="requestedBy"
                :to-show-name="false"
                :tooltip-content="`${requestedBy.name}, ${formatDateRefactored(createdAt)}`"
              />
            </template>
            <template #cell-createdByUser="{ row: { createdByUser } }">
              <v-account-owner-entity-info
                :account-owner="createdByUser"
                :to-show-name="false"
                :tooltip-content="`${createdByUser.name}`"
              />
            </template>
            <template #cell-status="{ row }">
              <status-badge
                :status="row.status || 'pending'"
                :data-cy="`${commonCy.ORDERS.TEXT__STATUS}-${row.id}`"
              />
            </template>
            <template #cell-type="{ row }">
              <v-badge class="title-badge">{{ titleCase(row.type) }}</v-badge>
            </template>
            <template #cell-processingStatus="{ row }">
              <img
                v-if="row.processingStatus === INVOICE_PROCESSING_STATUS_PROCESSING"
                src="@/assets/icons/ic_file-loading.svg"
                alt="Blue file icon with white loader"
                class="mr-4"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Processing',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
              />
              <img
                v-else-if="row.processingStatus === INVOICE_PROCESSING_STATUS_PROCESSED"
                src="@/assets/icons/ic_file-check.svg"
                alt="Green file icon with white check mark"
                class="mr-4"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Completed',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
              />
              <img
                v-else-if="row.processingStatus === INVOICE_PROCESSING_STATUS_ACTION_NEEDED"
                src="@/assets/icons/ic_file-exclamation.svg"
                alt="Red file icon with white exclamation mark"
                class="mr-4"
                v-tooltip="{
                  placement: 'top-end',
                  content: `${row.note ? row.note : ''}`,
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
              />
              <img
                v-else-if="row.processingStatus === INVOICE_PROCESSING_STATUS_DRAFT"
                src="@/assets/icons/ic_file-pending.svg"
                alt="Yellow icon with a clock"
                class="mr-4"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Draft',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
              />
            </template>
            <template #cell-exportStatus="{ row }">
              <img
                v-if="row.exportStatus === INVOICE_EXPORT_STATUS_READY"
                src="@/assets/icons/ic_export-ready.svg"
                alt="Blue cloud upload icon - export ready"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Export Ready',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
                @click.stop="exportOrder(row)"
              />
              <img
                v-else-if="row.exportStatus === 'loading'"
                src="@/assets/icons/ic_export-loading.svg"
                alt="Blue cloud upload icon - export loading"
                class="cursor-help"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Export Loading',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
              />
              <img
                v-else-if="row.exportStatus === INVOICE_EXPORT_STATUS_COMPLETED"
                src="@/assets/icons/ic_export-completed.svg"
                alt="Green cloud upload icon - export completed"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Export Completed',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
              />
              <img
                v-else-if="row.exportStatus === INVOICE_EXPORT_STATUS_MAPPING"
                src="@/assets/icons/ic_export-mapping.svg"
                alt="Yellow cloud upload icon - product mapping needed"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Product mapping needed',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
                @click.stop="openIntegrationMapping(row)"
              />
              <img
                v-else-if="row.exportStatus === INVOICE_EXPORT_STATUS_ERROR"
                src="@/assets/icons/ic_export-error.svg"
                alt="Red cloud upload icon - export error"
                v-tooltip="{
                  placement: 'top-end',
                  content: 'Export Error',
                  classes: ['tooltip-general', 'tooltip--lift-up'],
                }"
                @click.stop="exportOrder(row)"
              />
              <span v-else>-</span>
            </template>
            <template v-if="isInvoice" #cell-amount="{ row }">
              <v-price
                :price="row.amount || 0"
                :show-market-price-info="false"
                :is-to-be-determined="row.tbd"
                :data-cy="`${commonCy.ORDERS.TEXT__AMOUNT}-${row.id}`"
              />
            </template>
            <template v-else #cell-amount="{ row, row: { currency } }">
              <v-price
                :price="row.amountWithTax || 0"
                :currency="currency"
                :show-market-price-info="false"
                :is-to-be-determined="row.tbd"
                :data-cy="`${commonCy.ORDERS.TEXT__AMOUNT}-${row.id}`"
              />
            </template>
          </ez-table>
          <div class="order-table-mobile--b2b">
            <div
              v-for="order in group.items"
              :key="order.id"
              class="order-item-wrapper"
              :id="`order-item-${order.id}`"
              @click="onRowClick(order)"
            >
              <ez-entity-info
                v-if="order.distributor || order.groupVenue"
                imgWidth="2.125rem"
                imgHeight="2.125rem"
                :imgUrl="(order.distributor || order.groupVenue).logo"
                imgBorderRadius="50%"
                v-tooltip="{
                  placement: 'start',
                  content: (order.distributor || order.groupVenue).name,
                  classes: ['tooltip--reset-margin'],
                }"
              />
              <div class="order-item ml-8">
                <div class="first-row">
                  <span class="order-number" v-if="order.distributor || order.groupVenue">
                    {{ (order.distributor || order.groupVenue || {})?.name }}
                  </span>
                  <v-price
                    :price="isInvoice ? order.amount || 0 : order.amountWithTax || 0"
                    :currency="order.currency"
                    :show-market-price-info="false"
                    :is-to-be-determined="order.tbd"
                    :data-cy="`${commonCy.ORDERS.TEXT__AMOUNT}-${order.id}`"
                  />
                </div>
                <div class="second-row">
                  <div>
                    <span
                      class="order-number"
                      :data-cy="`${commonCy.ORDERS.TEXT__ORDER_NUMBER}-${order.id}`"
                      >{{ isInvoice ? order.invoiceNumber || '-' : order.orderNumber }}</span
                    >
                    <span class="mx-4">&#8226;</span>
                    <span
                      v-if="!isInvoice"
                      class="date"
                      :data-cy="`${commonCy.ORDERS.TEXT__ORDER_DATE}-${order.id}`"
                      >{{ order.orderedAt | date }}</span
                    >
                    <span v-else-if="order.invoiceDate" class="date">{{
                      order.invoiceDate | dateRefactored
                    }}</span>
                    <span v-else>-</span>
                  </div>
                  <div class="u-flex-row">
                    <status-badge
                      :status="isInvoice ? order.processingStatus : order.status || 'pending'"
                      :data-cy="`${commonCy.ORDERS.TEXT__STATUS}-${order.id}`"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isLoadingMore" class="u-text-center mt-12">
        <ez-spinner />
      </div>
      <ez-load-more
        v-if="!isLoadingMore && (meta.nextId || meta.lastPage > meta.page)"
        @loadMore="onLoadMoreClick"
      />
    </div>
    <template v-else-if="!isLoading">
      <div v-if="hasFilters">
        <empty-state class="order-list-no-orders">
          <template #badge><img src="@/assets/no-orders-illustration.svg" alt="" /></template>
          <template #title>No orders match these filters</template>
          <template #info>Try with different filters.</template>
        </empty-state>
      </div>
      <div v-else>
        <empty-state class="order-list-no-orders">
          <template #badge><img src="@/assets/no-orders-illustration.svg" alt="" /></template>
          <template #title>No orders</template>
          <template #info>All orders will appear here once they are created.</template>
        </empty-state>
      </div>
    </template>
    <ez-loader class="mt-12" :show="isLoading">Loading...</ez-loader>
    <slot></slot>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import VVenueEntityInfo from '@/components/v3/patterns/VVenueEntityInfo';
import VDistributorEntityInfo from '@/components/v3/patterns/VDistributorEntityInfo';
import EzEntityInfo from '@/components/ui/EntityInfo';
import EzFilterList from '@/components/ui/FilterList';
import EzTable from '@/components/ui/Table';
import EzLoadMore from '@/components/ui/LoadMore';
import StatusBadge from '@/views/common/status-badge';
import VBadge from '@/components/v3/elements/VBadge';
import EmptyState from '@/views/common/empty-state';
import Header from '@/views/platform/venue/orders/Header.vue';
import {
  LOADING_KEY,
  isAdmin,
  ORDER_STATUS_PENDING,
  INVOICE_PROCESSING_STATUS_PROCESSED,
  INVOICE_PROCESSING_STATUS_ACTION_NEEDED,
  INVOICE_PROCESSING_STATUS_PROCESSING,
  INVOICE_PROCESSING_STATUS_DRAFT,
  INVOICE_EXPORT_STATUS_READY,
  INVOICE_EXPORT_STATUS_ERROR,
  INVOICE_EXPORT_STATUS_COMPLETED,
  INVOICE_EXPORT_STATUS_MAPPING,
  ORDER_STATUS_QUOTE,
  ORDER_STATUS_BACKORDER,
  ORDER_STATUS_SENT,
} from '@/util/constants';
import { groupListByDateFormat, formatDateRefactored, isB2BPlatform } from '@/util/utils';
import { COMMON as commonCy } from '@orderez/qa-utils/dist/orderEz/common';
import EzLoader from '@/components/ui/Loader';
import EzSpinner from '@/components/ui/Spinner/EzSpinner';
import VPrice from '@/components/v3/elements/VPrice';
import VAccountOwnerEntityInfo from '@/components/v3/patterns/VAccountOwnerEntityInfo';
import { titleCase } from 'change-case';

export default {
  components: {
    EzSpinner,
    VDistributorEntityInfo,
    VVenueEntityInfo,
    EzEntityInfo,
    EzFilterList,
    EzLoadMore,
    EzTable,
    StatusBadge,
    VBadge,
    EmptyState,
    Header,
    EzLoader,
    VPrice,
    VAccountOwnerEntityInfo,
  },
  data() {
    return {
      ORDER_STATUS_PENDING,
      ORDER_STATUS_SENT,
      commonCy,
      INVOICE_PROCESSING_STATUS_PROCESSING,
      INVOICE_PROCESSING_STATUS_PROCESSED,
      INVOICE_PROCESSING_STATUS_ACTION_NEEDED,
      INVOICE_PROCESSING_STATUS_DRAFT,
      INVOICE_EXPORT_STATUS_READY,
      INVOICE_EXPORT_STATUS_ERROR,
      INVOICE_EXPORT_STATUS_COMPLETED,
      INVOICE_EXPORT_STATUS_MAPPING,
      ORDER_STATUS_QUOTE,
      ORDER_STATUS_BACKORDER,
      formatDateRefactored,
      isB2BPlatform,
      titleCase,
    };
  },
  props: {
    headers: {
      type: Object,
      default: () => ({
        orderNumber: () => 'Order No.',
        orderedAt: () => 'Order Date',
        amount: () => 'Total Amount',
        expectedAt: () => 'Delivery Date',
      }),
    },
    hasStats: {
      type: Boolean,
      default: true,
    },
    columns: {
      type: Array,
      required: true,
    },
    filters: {
      type: Object,
    },
    isLoadingMore: {
      required: false,
      type: Boolean,
      default: false,
    },
    showInvoiceNumber: {
      required: false,
      type: Boolean,
      default: false,
    },
    isInvoice: {
      required: false,
      type: Boolean,
      default: false,
    },
    groupByOrderDate: {
      required: false,
      type: Boolean,
      default: false,
    },
    defaultDateFilters: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    orders: {
      type: Array,
      required: false,
    },
  },
  computed: {
    isAdmin() {
      return isAdmin();
    },
    hasSlotFiltersGroup() {
      return !!this.$slots.filtersGroup;
    },
    hasFilters() {
      return Object.entries(this.filters).reduce(
        (prevFilter, [, value]) => prevFilter || !!value,
        false,
      );
    },
    hasOrders() {
      return Object.keys(this.groupedOrders).length;
    },
    ...mapGetters({
      stateGroupedOrders: 'entities/orders/getGroupedOrders',
    }),
    ...mapState('entities/orders', ['meta']),
    ...mapGetters('loading', ['isSomeLoading']),
    isLoading() {
      return this.isSomeLoading([
        LOADING_KEY.FETCH_ORDERS,
        LOADING_KEY.EXPORT_ORDER,
        LOADING_KEY.FETCH_ORDER,
      ]);
    },
    groupedOrders() {
      if (!this.orders?.length) return this.stateGroupedOrders;
      if (!this.isInvoice) {
        if (this.groupByOrderDate)
          return groupListByDateFormat({ list: this.orders, dateKey: 'orderedAt' });
        return groupListByDateFormat({ list: this.orders, dateKey: 'expectedAt' });
      }
      return groupListByDateFormat({ list: this.orders, dateKey: 'invoiceDate' });
    },
    totalLabel() {
      const appliedFilters = Object.entries(this.filters).filter(([key, val]) => {
        /**
         * Checks if the current values for date filters are the same as the default ones.
         * If not equal, that means the filter has changed and should be accounted for.
         */
        if (['scheduledFrom', 'scheduledTo', 'from', 'to'].includes(key)) {
          const filter = key.startsWith('scheduled') ? 'scheduled' : 'month';
          const property = key.split('scheduled').filter(Boolean)[0]; // 'from' | 'to'
          return val && val !== this.defaultDateFilters?.[filter]?.[property];
        }

        if (Array.isArray(val)) return !!val.length;
        return !!val;
      });

      return appliedFilters.length
        ? 'Total for selected filters'
        : 'Total for this month’s deliveries';
    },
  },
  methods: {
    resetFilters($event) {
      this.$emit('resetFilters', $event);
    },
    updateFilters(filterName, $event) {
      this.$emit('updateFilters', filterName, $event);
    },
    onRowClick($event) {
      this.$emit('rowClick', $event);
    },
    onRemoveRow($event) {
      this.$emit('removeItem', $event);
    },
    onLoadMoreClick() {
      this.$emit('loadMore');
    },
    exportOrder($event) {
      this.$emit('exportOrder', $event);
    },
    openIntegrationMapping($event) {
      this.$emit('openIntegrationMapping', $event);
    },
  },
};
</script>

<style scoped lang="scss">
.order-list {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: $loading-min-height;
  height: 100%;
  flex: 1 1 auto;

  &__header-wrapper :deep() {
    .order-header__stat {
      flex-direction: column-reverse;

      small {
        text-transform: initial;
      }
    }
  }

  &__content-wrapper {
    flex: 1 1 auto;
  }

  &__filters {
    display: flex;
    justify-content: space-between;
    padding: 1.5rem 0;
  }

  &__group {
    h2 {
      @include font-size(16px);
      font-weight: 500;
      margin: 0 0 1rem 0;
    }

    + .order-list__group {
      margin-top: 2rem;
    }
  }

  :deep() {
    .orderNumber-cell {
      span {
        display: block;

        &.order-number {
          font-weight: 600;
          color: $color-gray-25;
        }
      }
    }
  }
}

.order-list__status-icons {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
}

:deep() .order-list-no-orders {
  margin: 5rem auto;

  .ez-empty-state__image {
    max-width: none;
    img {
      margin-bottom: px-to-rem(24px);
      width: 219px;
      height: 116px;
    }
  }
}

.working-hours-flag {
  width: 12px;
  height: 12px;
  background-color: #4d7cfe;
}

:deep() .table tbody tr td:first-child {
  position: relative;
  overflow: hidden;
}

:deep() .table thead tr th.clickable-cell {
  cursor: pointer;
}

.text-overflow--clip {
  text-overflow: clip !important;
}

.show-on-hover {
  display: none;
}

.order-table-mobile--b2b {
  display: none;
  padding-bottom: 32px;
  width: 90vw;
  max-width: 100%;
  .order-item-wrapper {
    display: flex;
    align-items: center;
    border-bottom: 1px dashed #e1e5ed;
    &:hover {
      cursor: pointer;
      .show-on-hover {
        display: block;
      }
    }
  }

  .order-item {
    padding: 8px 0;
    width: 100%;
  }

  .first-row {
    display: flex;
    align-items: center;
    line-height: 1.5;
    justify-content: space-between;

    .order-number {
      font-weight: 600;
      font-size: 14px;
      line-height: 1.5;
    }
  }

  .second-row {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .date {
      font-size: 12px;
      line-height: 1.5;
      color: $color-gray-6C;
    }
  }

  .product-info {
    display: flex;
    flex-direction: column;
    line-height: 1.5;
    font-size: 14px;
    font-weight: 500;

    .product-info-secondary {
      display: block;
      font-size: 12px;
    }

    span {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}

@media (max-width: 1200px) {
  :deep() th.low-priority-cell,
  :deep() td.low-priority-cell {
    max-width: 80px;
  }

  :deep() th.venue-cell,
  :deep() td.venue-cell {
    width: 150px;
  }
}

@media (max-width: 1023px) {
  .order-list.order-list--b2b {
    :deep() .order-list__content table.table {
      display: none;
    }
    .orders-actions {
      display: none;
    }

    .order-table-mobile--b2b {
      display: block;
    }

    .order-list__filters {
      flex-wrap: wrap;
      gap: 12px;
    }
  }
}
</style>
